import { FieldValidator, useField, useFormikContext } from 'formik';
import { forwardRef, useMemo } from 'react';
import NumberFormat, { NumberFormatProps, NumberFormatValues } from 'react-number-format';

import { composeOptionalField } from '@jebel/utils';

import { composeValidators, mustBePhone, required } from 'shared/utils/form';

import { TextField, TextFieldProps } from '../TextField';
import { getIsInvalid } from '../common';

export interface FormPhoneFieldProps
  extends Omit<NumberFormatProps<Omit<TextFieldProps, 'inputProps'>>, 'prefix'> {
  /** Name of the field. */
  name?: string;

  validate?: FieldValidator;

  /**
   * Set the prefix for the phone number.
   * When set to `false`, the prefix will not be added to the value.
   * @default "+1"
   */
  prefix?: string | false;

  /** @deprecated Use `name`, `validate` or `required` instead. */
  fieldProps?: {
    name: string;
    validate?: FieldValidator;
    /** @deprecated Use `required` from outside instead. */
    required?: boolean;
  };

  /** @deprecated */
  inputProps?: NumberFormatProps<TextFieldProps>;
}

export const MASK_CHAR = '#';
export const PHONE_MASK = '###-###-####';
export const DEFAULT_PHONE_PREFIX = '+1';

export const FormPhoneField = forwardRef<HTMLInputElement, FormPhoneFieldProps>(
  (
    {
      fieldProps,
      inputProps,
      prefix = DEFAULT_PHONE_PREFIX,
      name = fieldProps?.name,
      required: isRequired = inputProps?.required ?? fieldProps?.required,
      validate,
      ...props
    },
    ref,
  ) => {
    const form = useFormikContext();

    const validation = composeValidators(
      isRequired ? required : undefined,
      composeOptionalField(mustBePhone),
      validate ?? fieldProps?.validate,
    );

    const [field, meta, helpers] = useField<string>({
      name: name as string,
      validate: validation,
    });

    const isInvalid = getIsInvalid({ meta, form });
    const errorText = isInvalid && meta.error;

    const value: string = useMemo(() => {
      if (!field.value || typeof field.value !== 'string') {
        return '';
      }

      return field.value.replace(`${prefix} `, '');
    }, [field.value, prefix]);

    const onChange = (values: NumberFormatValues) => {
      if (values.value === '') {
        helpers.setValue('');
        return;
      }

      const value = prefix ? `${prefix} ${values.value}` : values.value;
      helpers.setValue(value);
    };

    return (
      <NumberFormat<TextFieldProps>
        label="Phone Number"
        variant="outlined"
        {...props}
        {...inputProps}
        inputRef={ref}
        prefix={prefix || undefined}
        helperText={errorText}
        error={isInvalid}
        name={name}
        value={value}
        customInput={TextField}
        placeholder="800-000-0000"
        type="tel"
        mask="#"
        onValueChange={onChange}
        format={PHONE_MASK}
      />
    );
  },
);

/** @deprecated Use `FormPhoneField` instead. */
export const FormPhoneFieldV2 = FormPhoneField;
